/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListPluginsRequest,
  ListPluginsSecurity,
} from "../models/operations/listplugins.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildPluginsQuery,
  PluginsQueryData,
  prefetchPlugins,
  queryKeyPlugins,
} from "./plugins.core.js";
export {
  buildPluginsQuery,
  type PluginsQueryData,
  prefetchPlugins,
  queryKeyPlugins,
};

export type PluginsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listPlugins plugins
 *
 * @remarks
 * List all plugins for the current project.
 */
export function usePlugins(
  request?: ListPluginsRequest | undefined,
  security?: ListPluginsSecurity | undefined,
  options?: QueryHookOptions<PluginsQueryData, PluginsQueryError>,
): UseQueryResult<PluginsQueryData, PluginsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildPluginsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listPlugins plugins
 *
 * @remarks
 * List all plugins for the current project.
 */
export function usePluginsSuspense(
  request?: ListPluginsRequest | undefined,
  security?: ListPluginsSecurity | undefined,
  options?: SuspenseQueryHookOptions<PluginsQueryData, PluginsQueryError>,
): UseSuspenseQueryResult<PluginsQueryData, PluginsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildPluginsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setPluginsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: PluginsQueryData,
): PluginsQueryData | undefined {
  const key = queryKeyPlugins(...queryKeyBase);

  return client.setQueryData<PluginsQueryData>(key, data);
}

export function invalidatePlugins(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "listPlugins", ...queryKeyBase],
  });
}

export function invalidateAllPlugins(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "listPlugins"],
  });
}
